<template>
  <div
    class="case-line-image-input"
    :class="[{ 'add-image': !model && !loading, 'remove-image': model && !loading && !disabled }]"
    v-on:click="onClick">
    <div v-if="loading" class="loader"></div>
    <div v-else-if="model" class="preview-image" :style="{ backgroundImage: `url(${model.imageUrl})` }">
      <div class="icon-cnt">
        <backstage-icon v-if="!disabled" class="icon-delete" name="white-delete" />
      </div>
    </div>
    <input v-else-if="!disabled" :accept="accept" class="image-file-input" type="file" v-on:change="onChange" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ImagesService } from '@/services/images.service';
import BackstageIcon from '../../components/common/BackstageIcon.vue';

export default defineComponent({
  name: 'case-line-image-input',
  components: {
    'backstage-icon': BackstageIcon,
  },
  props: {
    model: {
      type: Object,
      default: undefined,
    },
    line: {
      type: Object,
      default: undefined,
    },
    delete: {
      type: Function,
      default: undefined,
    },
    change: {
      type: Function,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: 'image/*',
    },
    // maximum file size in MB
    maxFileSize: {
      type: Number,
      default: 11,
    },
  },
  data() {
    return {
      loading: false,
      imagesService: new ImagesService(),
    };
  },
  methods: {
    onClick() {
      if (this.$props.model?.imageId && !this.loading && !this.disabled) {
        this.loading = true;
        (this.$props.delete as Function)(this.$props.line, this.$props.model)?.then(() => (this.loading = false));
      }
    },
    onChange(e: Event) {
      const target = e.target as HTMLInputElement;
      const files = target.files as FileList;

      if (!files.length) {
        return;
      }

      const file = files[0] as File;

      const fileSize = file.size / 1048576;

      if (fileSize > this.maxFileSize) {
        this.$snotify.error(this.$t('viewCase.file_size_exceeded', { maxFileSize: this.maxFileSize }));

        return;
      }

      const fileReader = new FileReader();

      this.loading = true;

      fileReader.onerror = () => {
        this.loading = false;
      };

      fileReader.onload = () => {
        const data = fileReader.result;

        if (data) {
          (this.$props.change as Function)(this.$props.line, file).then(() => (this.loading = false));
        }
      };

      fileReader.readAsDataURL(file);
    },
  },
});
</script>

<style lang="scss">
#backstage-ui {
  .case-line-image-input {
    position: relative;
    display: inline-block;
    width: 80px;
    height: 80px;
    background-color: var(--bg-page-color);
    border: 1px dashed var(--gray-color);
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;

    &.remove-image {
      position: relative;

      .icon-cnt {
        display: none;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
        width: 42px;
        height: 42px;
        background-color: var(--secondary-bg-color);
        border-radius: 50%;
      }
      .icon-delete {
        width: 100%;
        height: 100%;
        background-size: 24px;
      }
      &:hover {
        .preview-image {
          .icon-cnt {
            display: block;
          }
        }
      }
    }

    &.add-image {
      &:before,
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: background-color 250ms ease;
        background-color: var(--primary-light-color);
      }

      &:hover {
        &:before,
        &:after {
          background-color: var(--primary-text-color);
        }
      }

      &:before {
        width: 1px;
        height: 38px;
      }

      &:after {
        width: 38px;
        height: 1px;
      }
    }

    .loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .preview-image {
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }

    .image-file-input {
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }
  }
}
</style>
